/**
 * Myanmar Standard Time (MST) is UTC+06:30 — no daylight-saving time.
 * All timestamps stored in the database are UTC ISO strings.
 * This module converts them to Myanmar time for display throughout the app.
 */

const MYANMAR_OFFSET_MS = (6 * 60 + 30) * 60 * 1000; // 6h 30m in ms

/**
 * Convert a UTC ISO timestamp string to a Date representing Myanmar local time.
 * Returns null for null/undefined/invalid input.
 */
export function toMyanmarTime(isoString: string | null | undefined): Date | null {
  if (!isoString) return null;
  try {
    const utcMs = new Date(isoString).getTime();
    if (isNaN(utcMs)) return null;
    // Shift by Myanmar offset so that the UTC fields of the resulting Date
    // correspond to Myanmar local time (we then read them without TZ adjustment)
    return new Date(utcMs + MYANMAR_OFFSET_MS);
  } catch {
    return null;
  }
}

/**
 * Format a UTC ISO datetime as "DD MMM YYYY, hh:mm AM/PM" in Myanmar time.
 * e.g. "09 Aug 2026, 10:30 AM"
 */
export function formatMyanmarDateTime(isoString: string | null | undefined): string {
  const d = toMyanmarTime(isoString);
  if (!d) return '—';
  // Read UTC fields which now represent Myanmar local time
  const day = String(d.getUTCDate()).padStart(2, '0');
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = monthNames[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  let hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

/**
 * Format a UTC ISO datetime as "DD MMM YYYY" (date only) in Myanmar time.
 * e.g. "09 Aug 2026"
 */
export function formatMyanmarDate(isoString: string | null | undefined): string {
  const d = toMyanmarTime(isoString);
  if (!d) return '—';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = monthNames[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

/**
 * Format a plain date string ("YYYY-MM-DD") for display as "DD MMM YYYY".
 * These fields are stored without time/timezone and should not be shifted.
 * e.g. "2026-08-09" → "09 Aug 2026"
 */
export function formatDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts.map(Number);
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(day).padStart(2, '0')} ${monthNames[month - 1]} ${year}`;
  } catch {
    return dateStr;
  }
}

/**
 * Format a UTC ISO datetime as a short "DD MMM YYYY" date in Myanmar time.
 * Alias for formatMyanmarDate — use when you only need the date portion of a timestamp.
 */
export function formatMyanmarDateShort(isoString: string | null | undefined): string {
  return formatMyanmarDate(isoString);
}
